import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import {
  AppRoutes,
  ERROR_INCORRECT_EMAIL_PASSWORD,
  ERROR_INVALID_FORMAT,
  ERROR_REQUIRED_FIELD,
} from '@/constants';
import { getCleanedPastedText, showAlertSuccess } from '@/utils';

import { EyeIcon, EyeOffIcon, LogoIcon } from '@/assets/icons';
import { Button, Input, Loading, SvgIcon } from '@/components/common';

import { useLoginWithUserNamePassword } from '@/features/logins/hooks';
import { LoginWithUserNamePasswordPayload } from '@/features/logins/interfaces';

const loginSchema = yup.object().shape({
  username: yup
    .string()
    .required(ERROR_REQUIRED_FIELD)
    .test('no-spaces', ERROR_INVALID_FORMAT, (value) => !/\s/.test(value ?? '')),
  password: yup.string().trim().required(ERROR_REQUIRED_FIELD),
}) as yup.ObjectSchema<LoginWithUserNamePasswordPayload>;

const PasswordLogin = () => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState<string>('');

  const { mutateAsync: loginMutation, isPending } = useLoginWithUserNamePassword();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginWithUserNamePasswordPayload>({
    resolver: yupResolver(loginSchema),
    mode: 'onSubmit',
  });

  const handleUsernamePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    setValue('username', getCleanedPastedText(e), { shouldDirty: true, shouldValidate: false });
  };

  const togglePasswordVisibility = () => setPasswordVisible((prev) => !prev);

  const onSubmit = async (data: LoginWithUserNamePasswordPayload) => {
    setUnauthorizedMessage('');
    try {
      await loginMutation({
        username: data.username,
        password: data.password,
      });
      showAlertSuccess('Login successful. Welcome to the Admin Portal.');
      navigate(AppRoutes.HOME);
    } catch (error) {
      if ((error as AxiosError).response?.status === 401) {
        setUnauthorizedMessage(ERROR_INCORRECT_EMAIL_PASSWORD);
      }
    }
  };

  return (
    <div className="w-[450px] max-w-full max-h-auto rounded-2xl p-12 bg-white flex flex-col justify-start items-center gap-10">
      <LogoIcon className="!w-[76px] !h-auto flex-shrink-0" />
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-start items-center gap-6"
      >
        <h5 className="text-xl font-bold text-brand-primary-darkest">Log In</h5>
        <span className="font-medium text-base text-brand-primary-darkest text-left">
          Log In with Your Username/Email and Password
        </span>
        <div className="gap-4 flex flex-col justify-start items-center">
          <Input
            required
            {...register('username')}
            error={errors.username?.message}
            placeholder="Username/Email"
            label="Username/Email"
            className="!w-[354px]"
            onPaste={handleUsernamePaste}
            style={{
              borderColor: unauthorizedMessage ? '#DC2020' : undefined,
            }}
          />
          <Input
            type={passwordVisible ? 'text' : 'password'}
            required
            {...register('password')}
            error={errors.password?.message}
            placeholder="Password"
            label="Password"
            postfixStyle="pointer-events-auto cursor-pointer !right-4"
            postfix={
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="flex items-center justify-center"
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              >
                <SvgIcon
                  component={passwordVisible ? EyeOffIcon : EyeIcon}
                  className="text-brand-primary-darkest !w-5 !h-5"
                />
              </button>
            }
            className="!w-[354px]"
            style={{
              borderColor: unauthorizedMessage ? '#DC2020' : undefined,
            }}
          />
        </div>
        {unauthorizedMessage && (
          <span className="text-xs text-semantic-error-base mr-auto">{unauthorizedMessage}</span>
        )}
        <Button variant="primary" size="lg" className="border-none w-[142px] mt-4" type="submit">
          Log In
        </Button>
      </form>
      <Button
        variant="link"
        className="!font-medium !text-base text-brand-primary-base underline !min-h-6"
        onClick={() => {
          navigate(`${AppRoutes.LOGIN_PAGE}?type=otp`);
        }}
      >
        Log In with OTP code
      </Button>
      {isPending && <Loading fullScreen />}
    </div>
  );
};

export default PasswordLogin;
